/**
 * Zones the picker names, in menu order. Asia/Shanghai predates the short list and stays
 * valid for users who picked it; the viewer's own zone is offered separately as "Local".
 */
export const REPORTING_TIME_ZONES = [
  "America/Los_Angeles",
  "America/New_York",
  "Europe/London",
  "Europe/Paris",
  "Australia/Sydney",
  "UTC",
  "Asia/Shanghai",
] as const;

/** Any IANA zone: reports resolve their date boundaries in it on the client. */
export type ReportingTimeZone = string;

export const DEFAULT_REPORTING_TIME_ZONE: ReportingTimeZone = "America/Los_Angeles";

export function isReportingTimeZone(value: unknown): value is ReportingTimeZone {
  if (typeof value !== "string" || value.length === 0) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

export function localTimeZone(): ReportingTimeZone {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_REPORTING_TIME_ZONE;
}

/** "Asia/Tokyo" → "Tokyo", "America/Argentina/Buenos_Aires" → "Buenos Aires". */
export function timeZoneCity(timeZone: ReportingTimeZone): string {
  return (timeZone.split("/").at(-1) ?? timeZone).replaceAll("_", " ");
}

interface ZonedDateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function zonedDateTimeParts(date: Date, timeZone: ReportingTimeZone): ZonedDateTimeParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((item) => item.type === type)?.value);

  return {
    year: part("year"),
    month: part("month"),
    day: part("day"),
    hour: part("hour"),
    minute: part("minute"),
    second: part("second"),
  };
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export function reportingDateInTimeZone(date: Date, timeZone: ReportingTimeZone): string {
  const parts = zonedDateTimeParts(date, timeZone);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

export function formatReportingClock(
  date: Date,
  timeZone: ReportingTimeZone,
): { abbreviation: string; dateTime: string } {
  const parts = zonedDateTimeParts(date, timeZone);
  const abbreviation =
    // Intl reports "CST" for Asia/Shanghai, which is byte-identical to what it
    // reports for America/Chicago — a 14-hour misread next to the PT option.
    timeZone === "Asia/Shanghai"
      ? "UTC+8"
      : (new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
          .formatToParts(date)
          .find((item) => item.type === "timeZoneName")?.value ?? "PT");

  return {
    abbreviation,
    dateTime: `${pad(parts.month)}-${pad(parts.day)} ${pad(parts.hour)}:${pad(parts.minute)}`,
  };
}

export function zonedStartOfDayUtc(isoDate: string, timeZone: ReportingTimeZone): Date {
  const [year, month, day] = isoDate.split("-").map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`Invalid ISO date: ${isoDate}`);
  }

  const targetTime = Date.UTC(year, month - 1, day);
  let candidateTime = targetTime;

  // Iterate because the UTC offset can change at daylight-saving boundaries.
  for (let iteration = 0; iteration < 3; iteration += 1) {
    const parts = zonedDateTimeParts(new Date(candidateTime), timeZone);
    const representedTime = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    );
    candidateTime += targetTime - representedTime;
  }

  return new Date(candidateTime);
}

export function addReportingDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
